import MuiDialog, { DialogProps as MuiDialogProps } from '@mui/material/Dialog';
import React from 'react';
import { CloseIcon } from '../../utility/Icon';
import { Box } from '../Box';
import { DialogTitle } from './DialogTitle';
import { styled } from '@invision/ui';

type PaperSlotProps = NonNullable<
  NonNullable<MuiDialogProps['slotProps']>['paper']
>;
type TransitionSlotProps = NonNullable<
  NonNullable<MuiDialogProps['slotProps']>['transition']
>;

export interface DialogProps extends Omit<MuiDialogProps, 'title'> {
  title: React.ReactNode;
  hideCloseButton?: boolean;
  /** @deprecated Use `slotProps.paper` instead. */
  PaperProps?: PaperSlotProps;
  /** @deprecated Use `slotProps.transition` instead. */
  TransitionProps?: TransitionSlotProps;
  disabled?: boolean;
}

const StyledCloseIcon = styled(CloseIcon)`
  font-size: 0.875rem;
`;

/**
 * Modal based on MateralUI Dialog component https://material-ui.com/components/dialogs/
 */
export function Dialog({
  title,
  children,
  disabled,
  maxWidth = 'sm',
  onClose,
  fullWidth,
  hideCloseButton = false,
  PaperProps,
  TransitionProps,
  slotProps,
  ...otherProps
}: DialogProps) {
  function handleCloseClick(event: any) {
    if (onClose) {
      onClose(event, 'escapeKeyDown');
    }
  }

  return (
    <MuiDialog
      data-cy="dialog"
      fullWidth
      maxWidth={maxWidth}
      slotProps={{
        ...slotProps,
        paper: {
          ...(typeof PaperProps === 'object' ? PaperProps : {}),
          ...(typeof slotProps?.paper === 'object' ? slotProps.paper : {}),
          ...(disabled ? { sx: { opacity: 0.8, pointerEvents: 'none' } } : {})
        } as PaperSlotProps,
        transition: {
          ...(typeof TransitionProps === 'object' ? TransitionProps : {}),
          ...(typeof slotProps?.transition === 'object'
            ? slotProps.transition
            : {})
        } as TransitionSlotProps
      }}
      {...otherProps}
    >
      {title && (
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Box
              data-cy="dialog-title"
              sx={{
                flexGrow: 1,
                maxWidth: '98%'
              }}
            >
              {title}
            </Box>
            {!hideCloseButton && <StyledCloseIcon onClick={handleCloseClick} />}
          </Box>
        </DialogTitle>
      )}
      {children}
    </MuiDialog>
  );
}
